import { BLUE_100, BLUE_600, GRAY_400, GRAY_900, WHITE } from 'constants/styling/theme'
import { FormControlLabel, FormControlLabelProps, Radio, RadioProps } from '@mui/material'
import React, { ReactNode } from 'react'

import { Label } from '../Label'
import { styled } from '@mui/material/styles'

/**
 * @interface Props Input properties
 */
export interface Props extends Omit<RadioProps, 'size'> {
  label?: string | ReactNode
  labelPlacement?: FormControlLabelProps['labelPlacement']
  size?: 'small' | 'medium' | 'large'
}

const StyledRadio = styled('span')<{ size?: 'small' | 'medium' | 'large' }>(({ size }) => {
  const dimensions = size === 'large' ? '2rem' : size === 'small' ? '1.2rem' : '1.6rem'
  return {
    width: dimensions,
    height: dimensions,
    borderRadius: '50%',
    border: `0.1rem solid ${GRAY_400}`,
    backgroundColor: WHITE,
    transition: '.3s ease',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    'input:hover ~ &': {
      borderColor: BLUE_600
    }
  }
})

const StyledCheckedRadio = styled(StyledRadio)<{ size?: 'small' | 'medium' | 'large' }>(({ size }) => {
  const dimensions = size === 'large' ? '2rem' : size === 'small' ? '1.2rem' : '1.6rem'
  return {
    backgroundColor: BLUE_100,
    borderColor: BLUE_600,
    '&:before': {
      display: 'block',
      width: dimensions,
      height: dimensions,
      backgroundImage: `radial-gradient(${BLUE_600},${BLUE_600} 28%,transparent 32%)`,
      content: '""',
    },
  }
})

/**
 * @component MUI Radio 
 * @example
 * <Radio checked={true} onChange={() => {}}/>
 */
export const MUIRadio: React.FC<Props> = ({
  checked,
  disabled,
  label,
  labelPlacement,
  onChange,
  size = 'medium',
  ...defaultProps
}) => {

  return (
    <FormControlLabel
      sx={{
        margin: 0,
        gap: '1rem',
        '.MuiFormControlLabel-label': {
          color: GRAY_900,
          fontSize: '1.2rem',
          lineHeight: 1,
        }
      }}
      control={(
        <Radio
          sx={{
            '&:hover': { bgcolor: 'transparent' },
            padding: 0
          }}
          checked={checked}
          disabled={disabled}
          disableRipple
          checkedIcon={<StyledCheckedRadio size={size} />}
          icon={<StyledRadio size={size} />}
          onChange={onChange}
          {...defaultProps}
        />
      )}
      label={typeof label === 'string' ? <Label text={label} /> : label}
      labelPlacement={labelPlacement}
    />
  )
}
